import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  ArrowBackOutlined,
  ArrowForwardOutlined,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';

import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Typography
} from '@mui/material';

import {
  menuProps,
  textFieldSx
} from '../../Common/components/Styles/InputStyles';

import {
  requiredSx,
  signInTitleSx,
  signInSubtitleSx,
  registrationCardSx,
  stepHeaderSx,
  stepNumberSx,
  stepTitleSx,
  formGridSx,
  fieldSx,
  fieldLabelSx,
  selectSx,
  formActionsSx,
  formControlSx,
  passwordIconButtonSx
} from '../../Common/components/Styles/AuthStyles';

const RegistrationForm = ({
  step,
  formValues,
  onChange,
  onNext,
  onPrevious
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const renderTextField = ({
    label,
    name,
    required = false,
    type = 'text',
    placeholder = ''
  }) => (
    <Box sx={fieldSx}>
      <Typography component="label" htmlFor={name} sx={fieldLabelSx}>
        {label}

        {required && (
          <Box component="span" sx={requiredSx}>
            *
          </Box>
        )}
      </Typography>

      <FormControl fullWidth variant="outlined" sx={formControlSx}>
        <OutlinedInput
          autoComplete="off"
          id={name}
          name={name}
          onChange={onChange}
          placeholder={placeholder}
          type={type}
          value={formValues[name]}
        />
      </FormControl>
    </Box>
  );

  const renderStepOne = () => (
    <Box sx={formGridSx}>
      {renderTextField({
        label: 'FIRST NAME',
        name: 'firstName',
        required: true,
        placeholder: 'Enter first name'
      })}

      {renderTextField({
        label: 'LAST NAME',
        name: 'lastName',
        required: true,
        placeholder: 'Enter last name'
      })}

      <Box
        sx={{
          ...fieldSx,
          gridColumn: '1 / -1'
        }}>
        <Typography sx={fieldLabelSx}>TITLE</Typography>

        <FormControl fullWidth sx={textFieldSx}>
          <Select
            name="title"
            value={formValues.title}
            onChange={onChange}
            displayEmpty
            renderValue={selected =>
              selected || (
                <Typography
                  component="span"
                  sx={{
                    color: '#757575',
                    fontSize: 12,
                    opacity: 0.7
                  }}>
                  Select title
                </Typography>
              )
            }
            MenuProps={menuProps}>
            <MenuItem disabled value="">
              Select title
            </MenuItem>

            <MenuItem value="Doctor of Orthopaedic Medicine">
              Doctor of Orthopaedic Medicine
            </MenuItem>

            <MenuItem value="Doctor of Medicine">Doctor of Medicine</MenuItem>

            <MenuItem value="Nurse Practitioner">Nurse Practitioner</MenuItem>

            <MenuItem value="Physician Assistant">Physician Assistant</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {renderTextField({
        label: 'BUSINESS PHONE',
        name: 'phone',
        placeholder: 'Enter business phone'
      })}

      {renderTextField({
        label: 'EMAIL ADDRESS',
        name: 'email',
        required: true,
        placeholder: 'Enter email address'
      })}
    </Box>
  );

  const renderStepTwo = () => (
    <Box sx={formGridSx}>
      {renderTextField({
        label: 'ORGANIZATION NAME',
        name: 'organizationName',
        placeholder: 'Enter organization name'
      })}

      {renderTextField({
        label: 'ORGANIZATION WEBSITE',
        name: 'organizationWebsite',
        placeholder: 'Enter organization website'
      })}

      {renderTextField({
        label: 'ADDRESS',
        name: 'address',
        placeholder: 'Enter address'
      })}

      {renderTextField({
        label: 'CITY',
        name: 'city',
        placeholder: 'Enter city'
      })}

      <Box sx={fieldSx}>
        <Typography sx={fieldLabelSx}>STATE</Typography>

        <FormControl fullWidth>
          <Select
            name="state"
            value={formValues.state}
            onChange={onChange}
            displayEmpty
            renderValue={selected =>
              selected || (
                <Typography
                  component="span"
                  sx={{
                    color: '#757575',
                    fontSize: 12,
                    opacity: 0.7
                  }}>
                  Select state
                </Typography>
              )
            }
            sx={selectSx}>
            <MenuItem disabled value="">
              Select state
            </MenuItem>

            <MenuItem value="California">California</MenuItem>

            <MenuItem value="Texas">Texas</MenuItem>

            <MenuItem value="Florida">Florida</MenuItem>

            <MenuItem value="New York">New York</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {renderTextField({
        label: 'ZIP',
        name: 'zip',
        placeholder: 'Enter ZIP code'
      })}

      <Box
        sx={{
          display: 'grid',
          gridColumn: '1 / -1',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 1.5,
          '@media (max-width: 700px)': {
            gridTemplateColumns: '1fr'
          }
        }}>
        {renderTextField({
          label: 'MEDICAL LICENSE#',
          name: 'medicalLicense',
          placeholder: 'Enter medical license #'
        })}

        {renderTextField({
          label: 'INDIVIDUAL NPI#',
          name: 'npi',
          placeholder: 'Enter individual NPI #'
        })}

        {renderTextField({
          label: 'DEA#',
          name: 'dea',
          placeholder: 'Enter DEA #'
        })}
      </Box>
    </Box>
  );

  const renderStepThree = () => (
    <Box
      sx={{
        ...formGridSx,
        gridTemplateColumns: '1fr'
      }}>
      {renderTextField({
        label: 'USERNAME',
        name: 'username',
        required: true,
        placeholder: 'Enter username'
      })}

      <Box sx={fieldSx}>
        <Typography component="label" htmlFor="password" sx={fieldLabelSx}>
          PASSWORD
          <Box component="span" sx={requiredSx}>
            *
          </Box>
        </Typography>

        <FormControl fullWidth variant="outlined" sx={formControlSx}>
          <OutlinedInput
            autoComplete="new-password"
            id="password"
            name="password"
            onChange={onChange}
            placeholder="Enter password"
            type={showPassword ? 'text' : 'password'}
            value={formValues.password}
            endAdornment={
              <IconButton
                edge="end"
                sx={passwordIconButtonSx}
                onClick={() => setShowPassword(current => !current)}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            }
          />
        </FormControl>
      </Box>

      <Box sx={fieldSx}>
        <Typography
          component="label"
          htmlFor="confirmPassword"
          sx={fieldLabelSx}>
          CONFIRM PASSWORD
          <Box component="span" sx={requiredSx}>
            *
          </Box>
        </Typography>

        <FormControl fullWidth variant="outlined" sx={formControlSx}>
          <OutlinedInput
            autoComplete="new-password"
            id="confirmPassword"
            name="confirmPassword"
            onChange={onChange}
            placeholder="Confirm password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formValues.confirmPassword}
            endAdornment={
              <IconButton
                edge="end"
                sx={passwordIconButtonSx}
                onClick={() => setShowConfirmPassword(current => !current)}>
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            }
          />
        </FormControl>
      </Box>
    </Box>
  );

  const titles = {
    1: 'Personal Information',
    2: 'Practice Information',
    3: 'Account Setup'
  };

  return (
    <Box sx={registrationCardSx}>
      <Typography sx={signInTitleSx}>Practice Registration</Typography>

      <Typography sx={signInSubtitleSx}>
        Start your free 14-day trial today. A credit card is required to
        activate your account. We’ll guide you through the next steps and send
        demo booking instructions via email.
      </Typography>

      <Box sx={stepHeaderSx}>
        <Typography sx={stepNumberSx}>STEP {step}/3</Typography>

        <Typography sx={stepTitleSx}>{titles[step]}</Typography>
      </Box>

      {step === 1 && renderStepOne()}

      {step === 2 && renderStepTwo()}

      {step === 3 && renderStepThree()}

      <Box sx={formActionsSx}>
        {step > 1 && (
          <Button
            startIcon={<ArrowBackOutlined />}
            onClick={onPrevious}
            variant="outlined">
            Previous
          </Button>
        )}

        <Button
          size="small"
          endIcon={<ArrowForwardOutlined />}
          onClick={onNext}
          variant="contained">
          {step === 3 ? 'Register' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

RegistrationForm.propTypes = {
  step: PropTypes.number.isRequired,

  formValues: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    title: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,

    organizationName: PropTypes.string,
    organizationWebsite: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    medicalLicense: PropTypes.string,
    npi: PropTypes.string,
    dea: PropTypes.string,

    username: PropTypes.string,
    password: PropTypes.string,
    confirmPassword: PropTypes.string
  }).isRequired,

  onChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired
};

export default RegistrationForm;
